import { effectiveDetailColumns } from './detailColumns';
import type {
  DatasetManifestEntry,
  DisplayColumn,
  ReportDefinition,
} from '../types/reporting';

const displayLabel = (field: string, dataset?: DatasetManifestEntry): string =>
  dataset?.columns.find(column => column.name === field)?.label ??
  (field.split('.').pop() ?? field)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter: string) => letter.toUpperCase());

const selectedFields = (
  definition: ReportDefinition,
  dataset?: DatasetManifestEntry
): string[] =>
  dataset ? effectiveDetailColumns(definition, dataset) : definition.columns;

const standalone = (
  field: string,
  dataset?: DatasetManifestEntry,
  columnLabels?: Map<string, string>
): DisplayColumn => ({
  label: columnLabels?.get(field) ?? displayLabel(field, dataset),
  fields: [field],
});

/**
 * Return the ordered display entries that the result table should render.
 *
 * `columns` is the authoritative selection. Display metadata can be stale while
 * a saved definition is being edited, so unchecked fields are removed and newly
 * checked fields are appended as standalone entries. A one-field persisted entry
 * is treated as a standalone field; named groups are the entries containing at
 * least two fields. This keeps schema v4 unchanged while making empty/draft
 * groups a UI concern only.
 */
export function effectiveDisplayColumns(
  definition: ReportDefinition,
  fields: string[],
  dataset?: DatasetManifestEntry,
  columnLabels?: Map<string, string>
): DisplayColumn[] {
  if (!definition.display_columns) {
    return fields.map(field => standalone(field, dataset, columnLabels));
  }

  const allowed = new Set(fields);
  const seen = new Set<string>();
  const entries: DisplayColumn[] = [];
  for (const column of definition.display_columns) {
    const valid = column.fields.filter(
      field => allowed.has(field) && !seen.has(field)
    );
    if (valid.length === 0) continue;
    valid.forEach(field => seen.add(field));
    entries.push(
      valid.length > 1
        ? { ...column, fields: valid }
        : standalone(valid[0], dataset, columnLabels)
    );
  }
  for (const field of fields) {
    if (!seen.has(field))
      entries.push(standalone(field, dataset, columnLabels));
  }
  return entries;
}

/**
 * Materialize the v4 save contract whenever display metadata is authored.
 * v3's empty columns plus `detail_columns_mode: all` cannot represent a
 * reordered/grouped selection, so v4 must carry the exact field list and must
 * not retain the v3 mode marker.
 */
const normalize = (
  definition: ReportDefinition,
  columns: DisplayColumn[]
): ReportDefinition => {
  const displayColumns = columns.filter(column => column.fields.length > 0);
  const next: ReportDefinition = {
    ...definition,
    schema_version: 4,
    columns: [...new Set(displayColumns.flatMap(column => column.fields))],
    display_columns: displayColumns,
  };
  delete next.detail_columns_mode;
  return next;
};

const materializedColumns = (
  definition: ReportDefinition,
  dataset?: DatasetManifestEntry
): DisplayColumn[] => {
  const selected = dataset
    ? selectedFields(definition, dataset)
    : definition.columns;
  return effectiveDisplayColumns(definition, selected, dataset);
};

export function addDisplayColumn(
  definition: ReportDefinition,
  label: string,
  fields: string[],
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const selected = dataset
    ? selectedFields(definition, dataset)
    : definition.columns;
  const unique = [...new Set(fields)].filter(field => selected.includes(field));
  if (unique.length < 2 || !label.trim()) return definition;

  const current = materializedColumns(definition, dataset);
  const moving = new Set(unique);
  const firstField = unique[0];
  let inserted = false;
  const positioned = current.flatMap(column => {
    const remaining = column.fields.filter(field => !moving.has(field));
    const result = remaining.length
      ? [
          remaining.length > 1
            ? { ...column, fields: remaining }
            : standalone(remaining[0], dataset),
        ]
      : [];
    if (column.fields.includes(firstField) && !inserted) {
      result.push({ label: label.trim(), fields: unique });
      inserted = true;
    }
    return result;
  });
  if (!inserted) positioned.push({ label: label.trim(), fields: unique });
  return normalize(definition, positioned);
}

/**
 * Delete a persisted entry. A populated group is not allowed to delete its
 * fields: they become standalone entries in the same position, so deleting a
 * heading never silently unchecks a report column.
 */
export function removeDisplayColumn(
  definition: ReportDefinition,
  index: number,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const columns = materializedColumns(definition, dataset);
  const target = columns[index];
  if (!target) return definition;
  const replacement = target.fields.map(field => standalone(field, dataset));
  return normalize(definition, [
    ...columns.slice(0, index),
    ...replacement,
    ...columns.slice(index + 1),
  ]);
}

export function updateDisplayColumnLabel(
  definition: ReportDefinition,
  index: number,
  label: string,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const columns = materializedColumns(definition, dataset);
  if (!columns[index] || !label.trim()) return definition;
  return normalize(
    definition,
    columns.map((column, i) =>
      i === index ? { ...column, label: label.trim() } : column
    )
  );
}

/**
 * Move one selected field into another display entry. The operation is used by
 * both dnd-kit and the accessible fallback controls, so pointer and keyboard
 * paths share exactly the same serialization behavior.
 */
export function moveDisplayColumnField(
  definition: ReportDefinition,
  fromIndex: number,
  field: string,
  toIndex: number,
  toPosition?: number,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const columns = materializedColumns(definition, dataset);
  const source = columns[fromIndex];
  const target = columns[toIndex];
  if (!source || !target || !source.fields.includes(field)) return definition;

  if (fromIndex === toIndex) {
    const from = source.fields.indexOf(field);
    const to = toPosition ?? from;
    if (from === to || to < 0 || to >= source.fields.length) return definition;
    const fields = [...source.fields];
    fields.splice(from, 1);
    fields.splice(to, 0, field);
    columns[fromIndex] =
      fields.length > 1
        ? { ...source, fields }
        : standalone(fields[0], dataset);
    return normalize(definition, columns);
  }

  const remaining = source.fields.filter(item => item !== field);
  const next = columns.flatMap((column, index) => {
    if (index !== fromIndex) return [column];
    return remaining.length > 1
      ? [{ ...column, fields: remaining }]
      : remaining.length === 1
        ? [standalone(remaining[0], dataset)]
        : [];
  });
  // Locate the original destination object after removing the source field.
  // The source entry may remain as a singleton, so arithmetic on the old index
  // would incorrectly target that replacement during cross-group moves.
  const adjustedTargetIndex = next.indexOf(target);
  const destination = next[adjustedTargetIndex];
  if (adjustedTargetIndex < 0 || !destination) return definition;
  const fields = [...destination.fields];
  const position = Math.max(
    0,
    Math.min(toPosition ?? fields.length, fields.length)
  );
  fields.splice(position, 0, field);
  next[adjustedTargetIndex] =
    fields.length > 1
      ? { ...destination, fields }
      : standalone(fields[0], dataset);
  return normalize(definition, next);
}

/** Move a field out of its group and make it a standalone entry. */
export function removeDisplayColumnField(
  definition: ReportDefinition,
  index: number,
  field: string,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const columns = materializedColumns(definition, dataset);
  const source = columns[index];
  if (!source?.fields.includes(field)) return definition;
  const remaining = source.fields.filter(item => item !== field);
  const sourceReplacement = remaining.length
    ? remaining.length > 1
      ? [{ ...source, fields: remaining }]
      : [standalone(remaining[0], dataset)]
    : [];
  return normalize(definition, [
    ...columns.slice(0, index),
    ...sourceReplacement,
    standalone(field, dataset),
    ...columns.slice(index + 1),
  ]);
}

/** Remove a child from its group and place it after every other top-level entry.
 * The field stays selected, so the minus action changes presentation only. */
export function moveDisplayColumnFieldToBottom(
  definition: ReportDefinition,
  index: number,
  field: string,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const without = removeDisplayColumnField(definition, index, field, dataset);
  if (without === definition) return definition;
  const columns = materializedColumns(without, dataset);
  const standaloneIndex = columns.findIndex(
    column => column.fields.length === 1 && column.fields[0] === field
  );
  if (standaloneIndex < 0 || standaloneIndex === columns.length - 1) {
    return without;
  }
  return moveDisplayColumn(
    without,
    standaloneIndex,
    columns.length - 1,
    dataset
  );
}

export function reorderDisplayColumnFields(
  definition: ReportDefinition,
  index: number,
  from: number,
  to: number,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const columns = materializedColumns(definition, dataset);
  const target = columns[index];
  if (
    !target ||
    from < 0 ||
    to < 0 ||
    from >= target.fields.length ||
    to >= target.fields.length ||
    from === to
  )
    return definition;
  const fields = [...target.fields];
  const [field] = fields.splice(from, 1);
  fields.splice(to, 0, field);
  columns[index] =
    fields.length > 1 ? { ...target, fields } : standalone(fields[0]);
  return normalize(definition, columns);
}

export function moveDisplayColumn(
  definition: ReportDefinition,
  from: number,
  to: number,
  dataset?: DatasetManifestEntry
): ReportDefinition {
  const columns = materializedColumns(definition, dataset);
  if (
    from < 0 ||
    to < 0 ||
    from >= columns.length ||
    to >= columns.length ||
    from === to
  )
    return definition;
  const [column] = columns.splice(from, 1);
  columns.splice(to, 0, column);
  return normalize(definition, columns);
}
